import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { DashboardLayout } from "../components/DashboardLayout";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender,
} from "@tanstack/react-table";
import { useProduct, type ProductPriceDetail, type ProductSubscriber, type SubscriptionStatus } from "../hooks/useProduct";

const columnHelper = createColumnHelper<ProductSubscriber>();

type SubscriberBadge = SubscriptionStatus | "trial";

const BADGE_STYLES: Record<SubscriberBadge, { label: string; className: string }> = {
  active: { label: "Active", className: "bg-green-100 text-green-800" },
  trial: { label: "Trial", className: "bg-amber-100 text-amber-800" },
  paused: { label: "Paused", className: "bg-gray-100 text-gray-600" },
  cancelled: { label: "Cancelled", className: "bg-gray-100 text-gray-600" },
};

/** A trialing subscriber is active, but the vendor needs to see they aren't paying yet. */
function subscriberBadge(row: ProductSubscriber): SubscriberBadge {
  return row.status === "active" && row.in_trial ? "trial" : row.status;
}

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(amount);
}

/** "14-day free trial" or "£1.00 for 14 days", or null when the price has no trial. */
function trialLabel(price: ProductPriceDetail): string | null {
  if (price.trial_days === null) return null;
  const days = `${price.trial_days} day${price.trial_days === 1 ? "" : "s"}`;
  return price.trial_amount === null
    ? `${price.trial_days}-day free trial`
    : `${formatCurrency(price.trial_amount, price.currency)} for ${days}`;
}

const subscriberColumns = [
  columnHelper.accessor((row) => `${row.forename} ${row.surname}`, {
    id: "name",
    header: "Name",
  }),
  columnHelper.accessor("email", {
    header: "Email",
  }),
  columnHelper.accessor("delivery_address", {
    header: "Delivery Address",
    cell: (info) => info.getValue() ?? "—",
  }),
  columnHelper.accessor(subscriberBadge, {
    id: "status",
    header: "Status",
    cell: (info) => {
      const badge = BADGE_STYLES[info.getValue()];
      return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
          {badge.label}
        </span>
      );
    },
  }),
  columnHelper.accessor("billing_date", {
    header: "Next Payment",
    cell: (info) => {
      const val = info.getValue();
      if (!val) return "-";
      const date = new Date(val).toLocaleDateString("en-GB");
      // For a trial this is the first payment, which is worth saying.
      return info.row.original.in_trial ? `${date} (first payment)` : date;
    },
  }),
  columnHelper.accessor("created_at", {
    header: "Subscribed",
    cell: (info) => new Date(info.getValue()).toLocaleDateString("en-GB"),
  }),
];

export function Product() {
  const { id } = useParams<{ id: string }>();
  const { data: product, isLoading, error } = useProduct(id!);

  useEffect(() => {
    document.title = product ? `${product.name} | PayWithPull` : "Product | PayWithPull";
  }, [product]);

  const table = useReactTable({
    data: product?.subscribers ?? [],
    columns: subscriberColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <p className="text-gray-400">Loading product...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !product) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <p className="text-red-500">{error?.message ?? "Product not found"}</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
        <div className="mb-6">
          <Link to="/dashboard" className="text-sm text-indigo-600 hover:text-indigo-800 transition-colors">
            &larr; Back to Dashboard
          </Link>
        </div>

        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-2xl font-bold text-gray-900">{product.name}</h1>
          {product.active ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Active
            </span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              Inactive
            </span>
          )}
        </div>
        <p className="text-gray-500 mb-8">{product.description}</p>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <p className="text-sm text-gray-500 mb-1">Prices</p>
            {product.prices.length === 0 ? (
              <p className="text-2xl font-semibold text-gray-900">—</p>
            ) : (
              <ul className="text-base font-semibold text-gray-900 space-y-0.5">
                {product.prices.map((p) => (
                  <li key={p.id}>
                    {formatCurrency(p.amount, p.currency)}
                    <span className="text-sm font-normal text-gray-400">
                      /{p.billing_interval_count === 1 ? p.billing_interval : `${p.billing_interval_count} ${p.billing_interval}s`}
                    </span>
                    {trialLabel(p) && (
                      <span className="block text-xs font-normal text-amber-700">{trialLabel(p)}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <p className="text-sm text-gray-500 mb-1">Active Subscribers</p>
            <p className="text-2xl font-semibold text-gray-900">{product.total_subscribers}</p>
            {product.trial_subscribers > 0 && (
              <p className="text-xs text-amber-700 mt-1">{product.trial_subscribers} on trial</p>
            )}
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <p className="text-sm text-gray-500 mb-1">Billing Period Revenue</p>
            <p className="text-2xl font-semibold text-gray-900">
              {formatCurrency(product.billing_period_revenue, product.prices[0]?.currency ?? "GBP")}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <p className="text-sm text-gray-500 mb-1">Created</p>
            <p className="text-2xl font-semibold text-gray-900">
              {new Date(product.created_at).toLocaleDateString("en-GB")}
            </p>
          </div>
        </div>

        {/* Subscribers Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Subscribers</h2>
          </div>
          {product.subscribers.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No subscribers yet.</div>
          ) : (
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="border-b border-gray-200 bg-gray-50">
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
    </DashboardLayout>
  );
}
